import { filterResponsiveColumns } from './responsiveTable'

export const SHOPPING_LIST_LOCKED_COLUMN_KEYS = ['title', 'action']

export const SHOPPING_LIST_OPTIONAL_COLUMN_DEFS = [
  { key: 'product.cloudinaryImageId', label: 'Image' },
  { key: 'zone', label: 'Zone' },
  { key: 'defaultQuantity', label: 'Default qty' },
  { key: 'parLevel', label: 'Par Level' },
  { key: 'excludeFromGrossProfit', label: 'Exclude from gross profit' },
  { key: 'product.size', label: 'Size', hideBelow: 1280 },
  { key: 'price', label: 'Price', requiresPrice: true },
  {
    key: 'specialPrice',
    label: 'Special price',
    hideBelow: 1280,
    requiresSpecialPrice: true,
  },
  { key: 'product.pricedBy', label: 'Priced By', hideBelow: 1280 },
  { key: 'supplierTitle', label: 'Supplier', hideBelow: 1093 },
  { key: 'product.sku', label: 'SKU', hideBelow: 1280 },
  { key: 'nominalCode', label: 'Nominal Code', hideBelow: 1366 },
  { key: 'product.GLN', label: 'GLN', hideBelow: 1366, requiresGln: true },
  { key: 'type', label: 'Type', hideBelow: 1093 },
  { key: 'Favourite', label: 'Favourite' },
]

export const SHOPPING_LIST_COLUMN_LABELS =
  SHOPPING_LIST_OPTIONAL_COLUMN_DEFS.reduce((labels, column) => {
    labels[column.key] = column.label
    return labels
  }, {})

const prefsKey = venueId =>
  `oms.shoppingList.hiddenColumns.${String(venueId || '').trim()}`

export const isShoppingListColumnLocked = key =>
  SHOPPING_LIST_LOCKED_COLUMN_KEYS.includes(String(key || ''))

export const shoppingListColumnLabel = column => {
  const key = String(column?.key || '')
  if (SHOPPING_LIST_COLUMN_LABELS[key]) return SHOPPING_LIST_COLUMN_LABELS[key]
  if (typeof column?.title === 'string' && column.title.trim()) {
    return column.title.trim()
  }
  return key
}

export const listShoppingListOptionalColumns = (
  columns = SHOPPING_LIST_OPTIONAL_COLUMN_DEFS,
  { showPrice = true, showSpecialPrice = true, showGln = true } = {},
) =>
  (Array.isArray(columns) ? columns : [])
    .filter(column => {
      if (!column?.key || isShoppingListColumnLocked(column.key)) return false
      if (column.requiresPrice && !showPrice) return false
      if (column.requiresSpecialPrice && !showSpecialPrice) return false
      if (column.requiresGln && !showGln) return false
      return true
    })
    .map(column => ({
      key: String(column.key),
      label: shoppingListColumnLabel(column),
    }))

export const loadShoppingListHiddenColumns = venueId => {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(prefsKey(venueId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed.map(String).filter(key => !isShoppingListColumnLocked(key))
  } catch (error) {
    return null
  }
}

export const saveShoppingListHiddenColumns = (venueId, hiddenKeys = []) => {
  if (typeof window === 'undefined' || !window.localStorage) return
  const next = (Array.isArray(hiddenKeys) ? hiddenKeys : [])
    .map(String)
    .filter(key => !isShoppingListColumnLocked(key))
  window.localStorage.setItem(prefsKey(venueId), JSON.stringify(next))
}

export const hiddenShoppingListColumnsFromWidth = (
  columns = [],
  windowWidth = 0,
) =>
  (Array.isArray(columns) ? columns : [])
    .filter(
      column =>
        column?.key &&
        !isShoppingListColumnLocked(column.key) &&
        Number(column.hideBelow) > 0 &&
        Number(windowWidth) < Number(column.hideBelow),
    )
    .map(column => String(column.key))

export const toggleShoppingListHiddenColumn = (hiddenKeys, columnKey) => {
  const key = String(columnKey || '')
  if (!key || isShoppingListColumnLocked(key)) {
    return Array.isArray(hiddenKeys) ? [...hiddenKeys] : []
  }
  const current = new Set(
    (Array.isArray(hiddenKeys) ? hiddenKeys : []).map(String),
  )
  if (current.has(key)) current.delete(key)
  else current.add(key)
  return [...current]
}

export const filterShoppingListVisibleColumns = (
  columns = [],
  { hiddenKeys, windowWidth } = {},
) => {
  const list = (Array.isArray(columns) ? columns : []).filter(
    column => column && column.key,
  )
  if (!Array.isArray(hiddenKeys)) {
    return filterResponsiveColumns(list, windowWidth)
  }
  const hidden = new Set(hiddenKeys.map(String))
  return list
    .filter(
      column =>
        isShoppingListColumnLocked(column.key) ||
        !hidden.has(String(column.key)),
    )
    .map(column => {
      const rest = { ...column }
      delete rest.hideBelow
      return rest
    })
}
